import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import { Button, Icon, SearchInput, Spinner } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

/**
 * Find a company or a student to bill, by name or email.
 *
 * Searches the admin directories — the same lists the Companies and Students
 * screens show — and returns the chosen record's id. `value` is the chosen
 * `{ id, label, detail }`, or `null`.
 */
export function CustomerPicker({ kind, value, onChange }) {
  const [search, setSearch] = useState('');
  const term = search.trim();

  const results = useQuery({
    queryKey: ['admin-customer-picker', kind, term],
    queryFn: () =>
      kind === 'student'
        ? adminApi.listStudents({ q: term, page_size: 6 })
        : adminApi.listCompanies({ q: term, page_size: 6 }),
    enabled: value === null && term.length >= 2,
    placeholderData: keepPreviousData,
  });

  if (value) {
    return (
      <div className="border-line flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
        <div className="min-w-0">
          <p className="text-ink truncate text-sm font-medium">{value.label}</p>
          {value.detail && <p className="text-ink-muted truncate text-xs">{value.detail}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    );
  }

  const rows = (results.data?.data ?? []).map((row) =>
    kind === 'student'
      ? { id: row.id, label: row.full_name, detail: row.email }
      : { id: row.id, label: row.name, detail: row.owner_email ?? row.slug },
  );

  return (
    <div className="space-y-2">
      <SearchInput
        label={kind === 'student' ? 'Find a student' : 'Find a company'}
        placeholder={kind === 'student' ? 'Student name or email' : 'Company name or slug'}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch('')}
      />
      {term.length < 2 ? (
        <p className="text-ink-muted text-xs">Type at least two letters.</p>
      ) : results.isError ? (
        <p className="text-danger-700 text-xs">{errorMessage(results.error)}</p>
      ) : results.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="text-ink-muted text-xs">Nobody matches “{term}”.</p>
      ) : (
        <ul
          className={cn(
            'border-line divide-line divide-y rounded-lg border',
            results.isPlaceholderData && 'opacity-60',
          )}
        >
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="hover:bg-surface-muted flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                onClick={() => onChange(row)}
              >
                <span className="min-w-0">
                  <span className="text-ink block truncate text-sm font-medium">{row.label}</span>
                  {row.detail && (
                    <span className="text-ink-muted block truncate text-xs">{row.detail}</span>
                  )}
                </span>
                <Icon name="plus" size={16} className="text-ink-muted shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
